/**
 * Business Development tracker — pre-pipeline prospect list.
 * Funnel: Not Contacted → Contacted → Meeting Requested → Meeting Done →
 * Interested → (convert) Pipeline Lead.
 *
 * Shared across all 4 contract divisions (LC, CC, CS) — scoped by division_id.
 */
import { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Trash2, ArrowRight, Filter } from 'lucide-react';
import api from '@/lib/api';

const STATUS_COLORS = {
  not_contacted: '#9CA3AF',
  contacted: '#3B82F6',
  meeting_requested: '#F59E0B',
  meeting_done: '#8B5CF6',
  interested: '#10B981',
  converted: '#6B7280',
  dropped: '#D1D5DB',
};

const fmtINR = (v) => {
  const n = Number(v) || 0;
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n.toLocaleString('en-IN');
};

export default function BDModule({ divisionId, divisionName }) {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [convertingId, setConvertingId] = useState(null);

  const refresh = async () => {
    const [pRes, sRes] = await Promise.all([
      api.get(`/bd?division_id=${divisionId}&include_converted=true`),
      api.get(`/bd/stats?division_id=${divisionId}`),
    ]);
    setProspects(pRes.data);
    setStats(sRes.data);
  };

  useEffect(() => {
    api.get('/bd/reference').then(r => {
      setStatuses(r.data.statuses);
      setSources(r.data.sources);
    });
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionId]);

  const owners = useMemo(() =>
    [...new Set(prospects.map(p => p.owner).filter(Boolean))].sort(),
    [prospects]);

  const filtered = prospects.filter(p =>
    (!filterStatus || p.status === filterStatus) &&
    (!filterOwner || p.owner === filterOwner)
  );

  const startAdd = () => {
    setEditing({
      division_id: divisionId,
      company: '', contact_person: '', contact_email: '', contact_phone: '',
      source: 'linkedin', status: 'not_contacted', owner: '',
      last_activity_date: '', next_action: '', next_action_date: '',
      estimated_value: '', notes: '',
    });
    setShowForm(true);
  };

  const startEdit = (p) => { setEditing({ ...p }); setShowForm(true); };

  const save = async () => {
    if (!editing.company.trim()) { window.alert('Company name is required'); return; }
    try {
      if (editing.id) {
        await api.put(`/bd/${editing.id}`, editing);
      } else {
        await api.post('/bd', editing);
      }
      setShowForm(false);
      setEditing(null);
      refresh();
    } catch (e) { window.alert(e.response?.data?.detail || e.message); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.company}" from BD pipeline?`)) return;
    await api.delete(`/bd/${p.id}`);
    refresh();
  };

  const convert = async (p) => {
    if (!window.confirm(`Convert "${p.company}" into a Pipeline Lead?\n\nThis creates a new pipeline_deals entry at "Lead" stage with all the BD info pre-filled.`)) return;
    setConvertingId(p.id);
    try {
      const res = await api.post(`/bd/${p.id}/convert`, {
        deal_value: Number(p.estimated_value) || 0,
        stage: 'Lead',
      });
      window.alert(res.data.message);
      refresh();
    } catch (e) { window.alert(e.response?.data?.detail || e.message); }
    finally { setConvertingId(null); }
  };

  return (
    <div className="space-y-5" data-testid="bd-module">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>Business Development — {divisionName}</h3>
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
            Prospecting tracker. The work BEFORE a lead enters Pipeline. Converts to Pipeline Lead in one click when interest is real.
          </p>
        </div>
        <button onClick={startAdd} className="flex items-center gap-1 px-3 py-2 rounded-md text-xs font-medium text-white"
          style={{ backgroundColor: '#2A7B88' }} data-testid="add-prospect-btn">
          <Plus size={12} /> Add Prospect
        </button>
      </div>

      {/* Funnel stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {['not_contacted', 'contacted', 'meeting_requested', 'meeting_done', 'interested', 'converted'].map(s => (
            <button key={s} onClick={() => setFilterStatus(filterStatus === s ? '' : s)}
              className={`text-left rounded-lg p-2 border transition ${filterStatus === s ? 'ring-2 ring-offset-1' : ''}`}
              style={{
                borderColor: '#E5E1DC',
                backgroundColor: 'white',
                ['--tw-ring-color']: STATUS_COLORS[s],
              }}>
              <p className="text-[10px] uppercase tracking-wide truncate" style={{ color: STATUS_COLORS[s] }}>
                {(statuses.find(x => x.id === s) || { label: s }).label.replace(' ⭐', '')}
              </p>
              <p className="text-xl font-semibold mt-0.5" style={{ color: '#1B2A4A' }}>{stats.by_status[s] || 0}</p>
            </button>
          ))}
        </div>
      )}

      {/* Per-owner table */}
      {stats && stats.by_owner && stats.by_owner.length > 0 && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
          <div className="px-4 py-2 font-semibold text-sm" style={{ backgroundColor: '#F9F7F4', color: '#1B2A4A' }}>
            This week — by owner
          </div>
          <table className="w-full text-xs">
            <thead style={{ backgroundColor: '#FAFAFA' }}>
              <tr>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Owner</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Total</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Contacted</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Mtg req</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Mtg done</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#10B981' }}>Interested</th>
                <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Converted</th>
              </tr>
            </thead>
            <tbody>
              {stats.by_owner.map(o => (
                <tr key={o.owner} className="border-t hover:bg-gray-50 cursor-pointer"
                  onClick={() => setFilterOwner(filterOwner === o.owner ? '' : o.owner)}
                  style={{ borderColor: '#F3F4F6', backgroundColor: filterOwner === o.owner ? '#EEF2FF' : 'transparent' }}>
                  <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{o.owner}</td>
                  <td className="px-3 py-2 text-right">{o.total}</td>
                  <td className="px-3 py-2 text-right">{o.contacted || 0}</td>
                  <td className="px-3 py-2 text-right">{o.meeting_requested || 0}</td>
                  <td className="px-3 py-2 text-right">{o.meeting_done || 0}</td>
                  <td className="px-3 py-2 text-right font-semibold" style={{ color: '#10B981' }}>{o.interested || 0}</td>
                  <td className="px-3 py-2 text-right" style={{ color: '#9CA3AF' }}>{o.converted || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      {(filterStatus || filterOwner) && (
        <div className="flex items-center gap-2 text-xs">
          <Filter size={12} style={{ color: '#6B7280' }} />
          {filterStatus && (
            <span className="px-2 py-0.5 rounded-md" style={{ backgroundColor: STATUS_COLORS[filterStatus] + '22', color: STATUS_COLORS[filterStatus] }}>
              Status: {(statuses.find(s => s.id === filterStatus) || {}).label}
              <button onClick={() => setFilterStatus('')} className="ml-1 font-bold">×</button>
            </span>
          )}
          {filterOwner && (
            <span className="px-2 py-0.5 rounded-md" style={{ backgroundColor: '#EEF2FF', color: '#3730A3' }}>
              Owner: {filterOwner}
              <button onClick={() => setFilterOwner('')} className="ml-1 font-bold">×</button>
            </span>
          )}
        </div>
      )}

      {/* Prospects table */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC', backgroundColor: 'white' }}>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: '#F9F7F4' }}>
            <tr>
              <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Company</th>
              <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Contact</th>
              <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Source</th>
              <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
              <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Owner</th>
              <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Est. Value</th>
              <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Next Action</th>
              <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-xs" style={{ color: '#9CA3AF' }}>
                No prospects yet. Click <strong>+ Add Prospect</strong> to start tracking who you&apos;re going after.
              </td></tr>
            )}
            {filtered.map(p => (
              <tr key={p.id} className="border-t hover:bg-gray-50" style={{ borderColor: '#F3F4F6' }} data-testid={`prospect-row-${p.id}`}>
                <td className="px-3 py-2">
                  <p className="font-medium text-sm" style={{ color: '#1B2A4A' }}>{p.company}</p>
                  {p.notes && <p className="text-[10px] truncate max-w-[200px]" style={{ color: '#9CA3AF' }} title={p.notes}>{p.notes}</p>}
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>
                  {p.contact_person || '—'}
                  {p.contact_email && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{p.contact_email}</p>}
                </td>
                <td className="px-3 py-2 text-xs capitalize" style={{ color: '#6B7280' }}>{p.source}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: STATUS_COLORS[p.status] + '22', color: STATUS_COLORS[p.status] }}>
                    {(statuses.find(s => s.id === p.status) || { label: p.status }).label}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: '#1B2A4A' }}>{p.owner || '—'}</td>
                <td className="px-3 py-2 text-xs text-right" style={{ color: '#1B2A4A' }}>{Number(p.estimated_value) > 0 ? `₹${fmtINR(p.estimated_value)}` : '—'}</td>
                <td className="px-3 py-2 text-xs" style={{ color: '#6B7280' }}>
                  {p.next_action || '—'}
                  {p.next_action_date && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>by {p.next_action_date}</p>}
                </td>
                <td className="px-3 py-2 text-right">
                  {p.status !== 'converted' && (
                    <button onClick={() => convert(p)} disabled={convertingId === p.id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded text-white mr-1 hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: '#10B981' }} data-testid={`convert-${p.id}`}
                      title="Convert this prospect to a Pipeline Lead">
                      <ArrowRight size={10} /> {convertingId === p.id ? '…' : 'Convert'}
                    </button>
                  )}
                  <button onClick={() => startEdit(p)} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded border mr-1 hover:bg-blue-50"
                    style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid={`edit-prospect-${p.id}`}>
                    <Pencil size={10} /> Edit
                  </button>
                  <button onClick={() => remove(p)} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded border hover:bg-red-50"
                    style={{ borderColor: '#FEE2E2', color: '#D4726A' }}>
                    <Trash2 size={10} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && editing && (
        <ProspectForm editing={editing} setEditing={setEditing} owners={owners}
          statuses={statuses} sources={sources}
          onSave={save} onClose={() => { setShowForm(false); setEditing(null); }} />
      )}
    </div>
  );
}


function ProspectForm({ editing, setEditing, owners, statuses, sources, onSave, onClose }) {
  const set = (k, v) => setEditing({ ...editing, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="prospect-form">
        <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>
          {editing.id ? 'Edit Prospect' : 'Add Prospect'}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Company *">
            <input value={editing.company} onChange={e => set('company', e.target.value)} className="w-full input" data-testid="company-name" />
          </Field>
          <Field label="Owner">
            <input list="bd-owners" value={editing.owner} onChange={e => set('owner', e.target.value)}
              placeholder="Srikanth · Nisha · Samar · Harshad · Amitabho" className="w-full input" />
            <datalist id="bd-owners">
              {owners.map(o => <option key={o} value={o} />)}
              <option value="Srikanth" />
              <option value="Nisha Verma" />
              <option value="Samar Singh Rathore" />
              <option value="Harshad" />
              <option value="Amitabho" />
            </datalist>
          </Field>
          <Field label="Contact person">
            <input value={editing.contact_person} onChange={e => set('contact_person', e.target.value)} className="w-full input" />
          </Field>
          <Field label="Contact email">
            <input value={editing.contact_email} onChange={e => set('contact_email', e.target.value)} className="w-full input" />
          </Field>
          <Field label="Contact phone">
            <input value={editing.contact_phone} onChange={e => set('contact_phone', e.target.value)} className="w-full input" />
          </Field>
          <Field label="Estimated value (₹)">
            <input type="number" value={editing.estimated_value} onChange={e => set('estimated_value', e.target.value)} className="w-full input" placeholder="0" />
          </Field>
          <Field label="Source">
            <select value={editing.source} onChange={e => set('source', e.target.value)} className="w-full input">
              {sources.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={editing.status} onChange={e => set('status', e.target.value)} className="w-full input">
              {statuses.filter(s => s.id !== 'converted').map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Last activity date">
            <input type="date" value={editing.last_activity_date} onChange={e => set('last_activity_date', e.target.value)} className="w-full input" />
          </Field>
          <Field label="Next action date">
            <input type="date" value={editing.next_action_date} onChange={e => set('next_action_date', e.target.value)} className="w-full input" />
          </Field>
          <Field label="Next action" full>
            <input value={editing.next_action} onChange={e => set('next_action', e.target.value)} placeholder="Send case study · Request intro from Microsoft contact · Follow up after Diwali" className="w-full input" />
          </Field>
          <Field label="Notes" full>
            <textarea value={editing.notes} onChange={e => set('notes', e.target.value)} rows={2} className="w-full input" />
          </Field>
        </div>
        <style>{`.input{padding:8px 10px;border:1px solid #E5E1DC;border-radius:6px;font-size:13px;}`}</style>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          <button onClick={onSave} className="px-4 py-2 rounded-md text-sm font-semibold text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="save-prospect-btn">
            {editing.id ? 'Update' : 'Add Prospect'}
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>{label}</label>
      {children}
    </div>
  );
}
